import { useState } from "react";
import { ChevronDown, FileText } from "lucide-react";

/* Phase 1 technical/sales resources — content limited to concepts supported by
   Zelligio's own product documentation (Product Data, General Installation,
   Care & Maintenance, Pool/Spa/Water-Feature Installation, Packaging/Transport/
   Storage/Receiving). Product-specific laboratory certifications are pending —
   no DCOF/ASTM/ANSI/freeze-thaw/slip-resistance/performance claims are made. */

const SECTIONS = [
  {
    key: "details",
    title: "Product details",
    points: [
      "Authentic handmade Moroccan Zellige — each piece is individually crafted.",
      "Natural variation in shade, glaze, reflectivity, texture, thickness, size, surface flatness, and edge geometry is expected and is part of the material's character.",
      "Product-specific laboratory certifications and performance testing are currently pending — no performance claims are made until testing completes.",
    ],
  },
  {
    key: "installation",
    title: "Application & installation",
    points: [
      "Inspect all material before installation — claims cannot be accepted after installation.",
      "Blend material from multiple cartons during installation for a balanced look.",
      "A representative mock-up is recommended before full installation.",
      "Use a qualified tile installer experienced with handmade tile.",
      "Tile and grout are finish materials — they are not the waterproofing system.",
      "In wet or submerged applications (showers, pools, spas, water features), use an appropriate waterproofing system and setting materials rated for the application.",
      "Always follow the installation-material manufacturer's instructions for setting materials, grout, and sealants.",
    ],
  },
  {
    key: "care",
    title: "Care & maintenance",
    points: [
      "Clean with pH-neutral cleaners suitable for handmade glazed tile.",
      "Avoid abrasive pads and harsh chemicals that can damage the glaze.",
      "Use sealants appropriate for the application where recommended by the installation-material manufacturer.",
    ],
  },
  {
    key: "handling",
    title: "Handling, storage & receiving",
    points: [
      "Inspect cartons on receipt and note any damage before signing.",
      "Store flat, dry, and protected from impact and weather until installation.",
      "Protect material during jobsite handling and from other trades.",
      "Preserve product and batch identification from cartons for any future claims or reorders.",
      "Retain spare material where practical for future repairs.",
    ],
  },
];

export function ProductResources() {
  const [open, setOpen] = useState(null);
  return (
    <div className="mt-8" data-testid="product-resources">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">
        Good to know — for you, your installer & your client
      </p>
      <div className="mt-3 divide-y divide-border rounded-md border border-border bg-white">
        {SECTIONS.map((s) => (
          <div key={s.key}>
            <button
              type="button"
              onClick={() => setOpen(open === s.key ? null : s.key)}
              className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-sand/50"
              data-testid={`resource-section-${s.key}`}
              aria-expanded={open === s.key}
            >
              <span className="flex items-center gap-2 text-sm font-medium text-charcoal">
                <FileText className="h-4 w-4 text-terracotta" /> {s.title}
              </span>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open === s.key ? "rotate-180" : ""}`} />
            </button>
            {open === s.key && (
              <ul className="space-y-1.5 px-4 pb-4 pl-10 text-sm text-warmgrey" data-testid={`resource-body-${s.key}`}>
                {s.points.map((p, i) => (
                  <li key={i} className="list-disc">{p}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Based on Zelligio product documentation. Installation guides and care documents are available from Zelligio on request.
      </p>
    </div>
  );
}
